import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader, Alert, Button } from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowRight,
  IconCheck,
  IconClock,
  IconLock,
  IconLayoutGrid,
  IconPlus,
  IconSearch,
  IconSparkles,
  IconStar,
  IconTrendingUp,
  IconUser,
  IconUsers,
} from '@tabler/icons-react';
import { useReview360Cycles, useReview360Dashboard } from '../../hooks/useApi';
import { useUserStore } from '../../store/userStore';
import { hasCompanyGrant, useMyPermissions } from '../../hooks/usePermission';
import { CycleCreateModal } from '../../components/review360/CycleCreateModal';
import {
  ReviewCycle,
  ReviewCycleStatus,
  Review360DashboardAction,
  Review360LatestResult,
  Review360RadarPoint,
} from '../../types';

type FilterValue = 'all' | 'mine' | ReviewCycleStatus;

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────
const STATUS_LABEL_KEY: Record<ReviewCycleStatus, string> = {
  draft: 'review360.dashboard.statusDraft',
  nomination: 'review360.dashboard.statusNomination',
  in_progress: 'review360.dashboard.statusInProgress',
  completed: 'review360.dashboard.statusCompleted',
  archived: 'review360.dashboard.statusArchived',
};

const STATUS_TONE: Record<ReviewCycleStatus, 'ghost' | 'warn' | 'accent' | 'neutral'> = {
  draft: 'ghost',
  nomination: 'warn',
  in_progress: 'accent',
  completed: 'neutral',
  archived: 'ghost',
};

const formatDateRu = (iso?: string): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' });
  } catch {
    return '—';
  }
};

const daysBetween = (iso?: string): number => {
  if (!iso) return 0;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
};

const initials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase();

// ────────────────────────────────────────────────────────────────────────────
// Hero
// ────────────────────────────────────────────────────────────────────────────
const Hero: React.FC<{
  pendingCount: number;
  canCreateCycle: boolean;
  canManageTemplates: boolean;
  onCreateCycle: () => void;
  onOpenTemplates: () => void;
}> = ({ pendingCount, canCreateCycle, canManageTemplates, onCreateCycle, onOpenTemplates }) => {
  const { t } = useTranslation();
  // Хедлайн hero — два варианта: «есть ожидания / всё закрыто». Подсветка числа
  // склеивается из t() и React-узла, поэтому собираем через split-плейсхолдер.
  const renderHeroTitle = () => {
    if (pendingCount === 0) return t('review360.dashboard.heroNothing');
    const tmpl = t('review360.dashboard.heroPending', { count: pendingCount });
    const m = tmpl.match(/^(.*)<accent>([^<]+)<\/accent>(.*)$/);
    if (!m) return tmpl;
    return (
      <>
        {m[1]}
        <span className="r360-hero-accent">{m[2]}</span>
        {m[3]}
      </>
    );
  };
  return (
    <div className="r360-hero">
      <div>
        <div className="r360-eyebrow" style={{ marginBottom: 8 }}>
          {t('review360.dashboard.heroEyebrow')}
        </div>
        <h1>{renderHeroTitle()}</h1>
        <p>{t('review360.dashboard.heroSubtitle')}</p>
      </div>
      {(canCreateCycle || canManageTemplates) && (
        <div className="r360-hero-actions">
          {canManageTemplates && (
            <Button
              variant="default"
              leftSection={<IconLayoutGrid size={14} />}
              onClick={onOpenTemplates}
            >
              {t('review360.dashboard.btnTemplates')}
            </Button>
          )}
          {canCreateCycle && (
            <Button leftSection={<IconPlus size={14} />} variant="light" onClick={onCreateCycle}>
              {t('review360.dashboard.btnCreateCycle')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Stat strip
// ────────────────────────────────────────────────────────────────────────────
interface StatTile {
  label: string;
  value: number;
  sub: string;
  tone: 'accent' | 'warn' | 'neutral';
}
const StatStrip: React.FC<{ stats: StatTile[] }> = ({ stats }) => (
  <div className="r360-stats">
    {stats.map((s) => (
      <div key={s.label} className="r360-card r360-stat">
        <div className="r360-stat-head">
          <span className="r360-eyebrow">{s.label}</span>
          <span className={`r360-dot r360-dot-${s.tone}`} />
        </div>
        <div className="num r360-stat-value">{s.value}</div>
        <div className="r360-stat-sub">{s.sub}</div>
      </div>
    ))}
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// Actions block
// ────────────────────────────────────────────────────────────────────────────
const ActionsBlock: React.FC<{
  actions: Review360DashboardAction[];
  onOpenAction: (a: Review360DashboardAction) => void;
}> = ({ actions, onOpenAction }) => {
  const { t } = useTranslation();
  if (!actions.length) return null;
  return (
    <div className="r360-card">
      <div className="r360-card-head">
        <div>
          <div className="r360-eyebrow">{t('review360.dashboard.actionsEyebrow')}</div>
          <h2 className="r360-card-title">{t('review360.dashboard.actionsTitle')}</h2>
        </div>
        <span className="r360-hint">
          {t('review360.dashboard.actionsCount', { count: actions.length })}
        </span>
      </div>
      <div className="r360-actions">
        {actions.map((a, i) => (
          <ActionRow
            key={a.id}
            a={a}
            isLast={i === actions.length - 1}
            onClick={() => onOpenAction(a)}
          />
        ))}
      </div>
    </div>
  );
};

const ActionRow: React.FC<{
  a: Review360DashboardAction;
  isLast: boolean;
  onClick: () => void;
}> = ({ a, isLast, onClick }) => {
  const { t } = useTranslation();
  const isUrgent = (a.daysLeft ?? 0) <= 5 && (a.daysLeft ?? 0) > 0;
  const filled =
    a.answered != null && a.questions != null && a.questions > 0 ? a.answered / a.questions : null;
  const kindMeta: Record<
    Review360DashboardAction['kind'],
    { label: string; icon: React.ReactNode; tone: 'accent' | 'warn' | 'neutral' }
  > = {
    rate: {
      label: t('review360.dashboard.actionKindRate'),
      icon: <IconUsers size={16} />,
      tone: 'accent',
    },
    self: {
      label: t('review360.dashboard.actionKindSelf'),
      icon: <IconUser size={16} />,
      tone: 'neutral',
    },
    nominate: {
      label: t('review360.dashboard.actionKindNominate'),
      icon: <IconStar size={16} />,
      tone: 'warn',
    },
  };
  const k = kindMeta[a.kind];

  return (
    <button
      type="button"
      onClick={onClick}
      className={`r360-action${isLast ? ' r360-action-last' : ''}`}
    >
      <div className={`r360-action-icon r360-action-icon-${k.tone}`}>{k.icon}</div>
      <div className="r360-action-body">
        <div className="r360-action-title">{a.subjectName || '—'}</div>
        <div className="r360-action-meta">
          <span>{a.cycleTitle}</span>
          {a.role && (
            <>
              <span className="r360-dot-mini" />
              <span>{a.role}</span>
            </>
          )}
        </div>
      </div>
      <div className="r360-action-progress">
        {filled != null ? (
          <>
            <div className="r360-action-progress-head">
              <span className="r360-hint">{k.label}</span>
              <span className="num r360-action-progress-count">
                {a.answered}
                <span className="r360-action-progress-of">/{a.questions}</span>
              </span>
            </div>
            <div className="r360-progress">
              <div className="r360-progress-fill" style={{ width: `${filled * 100}%` }} />
            </div>
          </>
        ) : (
          <span className="r360-pill r360-pill-warn">{k.label}</span>
        )}
      </div>
      <div className="r360-action-deadline">
        <span className={`r360-pill ${isUrgent ? 'r360-pill-warn' : 'r360-pill-ghost'}`}>
          <IconClock size={11} />{' '}
          {(a.daysLeft ?? 0) <= 0
            ? t('review360.dashboard.actionOverdue')
            : t('review360.dashboard.actionDeadline', {
                days: a.daysLeft,
                date: formatDateRu(a.deadline || undefined),
              })}
        </span>
      </div>
      <div className="r360-action-cta">
        <span className="r360-action-cta-text">
          {filled === 1
            ? t('review360.dashboard.actionCtaOpen')
            : (filled ?? 0) > 0
              ? t('review360.dashboard.actionCtaContinue')
              : a.kind === 'nominate'
                ? t('review360.dashboard.actionCtaNominate')
                : t('review360.dashboard.actionCtaStart')}
        </span>
        <IconArrowRight size={12} />
      </div>
    </button>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Filter bar
// ────────────────────────────────────────────────────────────────────────────
const FilterBar: React.FC<{
  filter: FilterValue;
  onChange: (v: FilterValue) => void;
  search: string;
  onSearch: (v: string) => void;
  counts: Record<FilterValue, number>;
}> = ({ filter, onChange, search, onSearch, counts }) => {
  const { t } = useTranslation();
  const tabs: Array<{ v: FilterValue; label: string }> = [
    { v: 'all', label: t('review360.dashboard.tabAll') },
    { v: 'mine', label: t('review360.dashboard.tabMine') },
    { v: 'in_progress', label: t('review360.dashboard.tabInProgress') },
    { v: 'nomination', label: t('review360.dashboard.tabNomination') },
    { v: 'completed', label: t('review360.dashboard.tabCompleted') },
    { v: 'archived', label: t('review360.dashboard.tabArchived') },
  ];
  return (
    <div className="r360-filter-bar">
      <div className="r360-tabs">
        {tabs.map((t) => (
          <button
            key={t.v}
            type="button"
            onClick={() => onChange(t.v)}
            className={`r360-tab${filter === t.v ? ' r360-tab-active' : ''}`}
          >
            {t.label}
            <span className="num r360-tab-count">{counts[t.v]}</span>
          </button>
        ))}
      </div>
      <div className="r360-search">
        <IconSearch size={14} />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={t('review360.dashboard.searchPlaceholder')}
        />
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Cycle card
// ────────────────────────────────────────────────────────────────────────────
const CycleCard: React.FC<{
  cycle: ReviewCycle;
  iAmSubject: boolean;
  onOpen: () => void;
}> = ({ cycle, iAmSubject, onOpen }) => {
  const { t } = useTranslation();
  const status = cycle.status;
  const tone = STATUS_TONE[status];
  const isActive = status === 'in_progress' || status === 'nomination';
  const subjects = cycle.subjects || [];
  const dl = status === 'nomination' ? cycle.deadlines?.nomination : cycle.deadlines?.response;
  const days = daysBetween(dl);
  const startDate = formatDateRu(cycle.createdAt);
  const endDate = formatDateRu(dl);

  return (
    <button type="button" onClick={onOpen} className="r360-cycle">
      <div className="r360-cycle-main">
        <div className="r360-cycle-info">
          <div className="r360-cycle-pills">
            <span className={`r360-pill r360-pill-${tone}`}>
              <span className={`r360-dot r360-dot-${tone}`} /> {t(STATUS_LABEL_KEY[status])}
            </span>
            <span className="r360-pill r360-pill-ghost">
              {cycle.type === '360'
                ? t('review360.dashboard.cycleType360')
                : t('review360.dashboard.cycleType180')}
            </span>
            {iAmSubject && (
              <span className="r360-pill r360-pill-accent">
                <IconSparkles size={10} /> {t('review360.dashboard.pillAboutMe')}
              </span>
            )}
            {cycle.anonymitySettings?.managerAnonymous && (
              <span className="r360-pill r360-pill-ghost">
                <IconLock size={10} /> {t('review360.dashboard.pillAnonymous')}
              </span>
            )}
          </div>
          <h3 className="r360-cycle-title">{cycle.title}</h3>
          <div className="r360-cycle-meta">
            <span className="num">{startDate}</span>
            <span>—</span>
            <span className="num">{endDate}</span>
            <span className="r360-dot-mini" />
            <span>
              {t('review360.dashboard.cycleCreatedBy', {
                name: cycle.createdBy?.name || '—',
              })}
            </span>
          </div>
        </div>

        <div className="r360-cycle-subjects">
          <div className="r360-eyebrow" style={{ marginBottom: 8 }}>
            {t('review360.dashboard.subjectsLabel', { count: subjects.length })}
          </div>
          <div className="r360-avatars">
            {subjects.slice(0, 4).map((s, i) => (
              <div
                key={s._id || i}
                className="r360-avatar"
                title={s.name}
                style={{ marginLeft: i === 0 ? 0 : -8, zIndex: 4 - i }}
              >
                {s.avatar ? (
                  <img src={s.avatar} alt={s.name} />
                ) : (
                  <span>{initials(s.name || '?')}</span>
                )}
              </div>
            ))}
            {subjects.length > 4 && (
              <div className="r360-avatar-extra num">+{subjects.length - 4}</div>
            )}
          </div>
        </div>

        <div className="r360-cycle-side">
          {isActive && days > 0 && (
            <span className={`r360-pill ${days <= 5 ? 'r360-pill-warn' : 'r360-pill-accent'}`}>
              <IconClock size={11} />{' '}
              {t('review360.dashboard.daysToDeadline', { days, date: endDate })}
            </span>
          )}
          {status === 'completed' && (
            <span className="r360-pill r360-pill-accent">
              <IconCheck size={11} /> {t('review360.dashboard.pillCompleted')}
            </span>
          )}
          <span className={`r360-cta ${isActive ? 'r360-cta-primary' : 'r360-cta-secondary'}`}>
            {status === 'completed' && iAmSubject
              ? t('review360.dashboard.ctaCompleted')
              : status === 'nomination' && iAmSubject
                ? t('review360.dashboard.ctaNominate')
                : t('review360.dashboard.ctaOpen')}
            <IconArrowRight size={12} />
          </span>
        </div>
      </div>
    </button>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Radar chart (latest result)
// ────────────────────────────────────────────────────────────────────────────
const Radar: React.FC<{ data: Review360RadarPoint[]; size?: number }> = ({ data, size = 260 }) => {
  const { t } = useTranslation();
  if (data.length < 3) {
    return <div className="r360-radar-empty">{t('review360.dashboard.radarEmpty')}</div>;
  }
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 44;
  const n = data.length;
  const pt = (a: number, d: number): [number, number] => [
    cx + Math.cos(a - Math.PI / 2) * d,
    cy + Math.sin(a - Math.PI / 2) * d,
  ];
  const angles = data.map((_, i) => (i / n) * Math.PI * 2);
  const selfPts = data.map((d, i) => pt(angles[i], r * (d.self / 5)));
  const othersPts = data.map((d, i) => pt(angles[i], r * (d.others / 5)));
  const rings = [0.25, 0.5, 0.75, 1.0];
  const padX = 50;

  return (
    <svg
      width={size + padX * 2}
      height={size}
      viewBox={`${-padX} 0 ${size + padX * 2} ${size}`}
      style={{ overflow: 'visible', maxWidth: '100%', display: 'block', margin: '0 auto' }}
    >
      {rings.map((rr, i) => (
        <polygon
          key={i}
          points={data.map((_, j) => pt(angles[j], r * rr).join(',')).join(' ')}
          fill="none"
          stroke="var(--r360-line)"
          strokeDasharray={i === rings.length - 1 ? '0' : '2 4'}
        />
      ))}
      {angles.map((a, i) => {
        const [x, y] = pt(a, r);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--r360-line)" />;
      })}
      <polygon
        points={othersPts.map((p) => p.join(',')).join(' ')}
        fill="var(--r360-accent)"
        fillOpacity={0.2}
        stroke="var(--r360-accent)"
        strokeWidth={2}
      />
      <polygon
        points={selfPts.map((p) => p.join(',')).join(' ')}
        fill="none"
        stroke="var(--r360-ink-2)"
        strokeWidth={1.4}
        strokeDasharray="4 4"
      />
      {othersPts.map(([x, y], i) => (
        <circle
          key={i}
          cx={x}
          cy={y}
          r={3.5}
          fill="var(--r360-surface)"
          stroke="var(--r360-accent)"
          strokeWidth={2}
        />
      ))}
      {angles.map((a, i) => {
        const [x, y] = pt(a, r + 18);
        const anchor = x < cx - 5 ? 'end' : x > cx + 5 ? 'start' : 'middle';
        return (
          <text
            key={i}
            x={x}
            y={y}
            fontSize="10.5"
            fontWeight={500}
            fill="var(--r360-ink-2)"
            textAnchor={anchor}
            dominantBaseline="central"
          >
            {data[i].axis}
          </text>
        );
      })}
    </svg>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Sidebar cards
// ────────────────────────────────────────────────────────────────────────────
const LatestResultCard: React.FC<{
  result: Review360LatestResult;
  onOpen: () => void;
}> = ({ result, onOpen }) => {
  const { t } = useTranslation();
  return (
    <div className="r360-card" style={{ padding: 24 }}>
      <div className="r360-section-head">
        <div className="r360-eyebrow">{t('review360.dashboard.latestResultEyebrow')}</div>
        {result.delta != null && (
          <span
            className={`r360-pill ${result.delta >= 0 ? 'r360-pill-accent' : 'r360-pill-warn'}`}
          >
            <IconTrendingUp size={11} />
            {result.delta >= 0 ? '+' : ''}
            {result.delta.toFixed(1)}
          </span>
        )}
      </div>
      <h3 className="r360-result-title">{result.cycleTitle}</h3>
      <div className="r360-hint" style={{ marginBottom: 14 }}>
        {result.cycleDate}
      </div>
      <Radar data={result.radar} />
      <div className="r360-result-footer">
        <div>
          <div className="r360-eyebrow">{t('review360.dashboard.latestResultAvg')}</div>
          <div className="r360-result-score num">
            {result.score.toFixed(1)}
            <span className="r360-result-score-max"> / 5.0</span>
          </div>
        </div>
        <Button
          variant="subtle"
          size="xs"
          rightSection={<IconArrowRight size={12} />}
          onClick={onOpen}
        >
          {t('review360.dashboard.latestResultFullReport')}
        </Button>
      </div>
    </div>
  );
};

const HowItWorksCard: React.FC = () => {
  const { t } = useTranslation();
  const items = [
    {
      n: 1,
      title: t('review360.dashboard.hiw1Title'),
      desc: t('review360.dashboard.hiw1Desc'),
    },
    {
      n: 2,
      title: t('review360.dashboard.hiw2Title'),
      desc: t('review360.dashboard.hiw2Desc'),
    },
    {
      n: 3,
      title: t('review360.dashboard.hiw3Title'),
      desc: t('review360.dashboard.hiw3Desc'),
    },
    {
      n: 4,
      title: t('review360.dashboard.hiw4Title'),
      desc: t('review360.dashboard.hiw4Desc'),
    },
  ];
  return (
    <div className="r360-card" style={{ padding: 24 }}>
      <div className="r360-eyebrow">{t('review360.dashboard.hiwEyebrow')}</div>
      <div className="r360-hiw">
        {items.map((it) => (
          <div key={it.n} className="r360-hiw-row">
            <div className="r360-hiw-num num">{it.n}</div>
            <div>
              <div className="r360-hiw-title">{it.title}</div>
              <div className="r360-hiw-desc">{it.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────
export const Review360Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useUserStore((s) => s.user);
  // Кнопки — по правам с сервера: цикл запускает держатель review360.run хоть на кого-то
  // (руководитель — по своим людям), шаблоны — review360.manage на компанию.
  const { data: permissions } = useMyPermissions();
  const canCreateCycle = !!permissions?.grants['review360.run'];
  const canManageTemplates = hasCompanyGrant(permissions, 'review360.manage');
  const { data: cycles = [], isLoading, error } = useReview360Cycles();
  const { data: dashboard } = useReview360Dashboard();

  const [filter, setFilter] = useState<FilterValue>('all');
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const meId = me?._id;
  const isSubjectIn = (c: ReviewCycle) => !!meId && (c.subjects || []).some((s) => s._id === meId);

  const counts = useMemo<Record<FilterValue, number>>(
    () => ({
      all: cycles.length,
      mine: cycles.filter((c) => isSubjectIn(c)).length,
      draft: cycles.filter((c) => c.status === 'draft').length,
      in_progress: cycles.filter((c) => c.status === 'in_progress').length,
      nomination: cycles.filter((c) => c.status === 'nomination').length,
      completed: cycles.filter((c) => c.status === 'completed').length,
      archived: cycles.filter((c) => c.status === 'archived').length,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cycles, meId],
  );

  const filtered = useMemo(() => {
    let arr = cycles;
    if (filter === 'mine') arr = arr.filter(isSubjectIn);
    else if (filter !== 'all') arr = arr.filter((c) => c.status === filter);
    const q = search.trim().toLowerCase();
    if (q) arr = arr.filter((c) => c.title.toLowerCase().includes(q));
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycles, filter, search, meId]);

  const stats: StatTile[] = useMemo(() => {
    const s = dashboard?.stats;
    return [
      {
        label: t('review360.dashboard.statPending'),
        value: s?.pending ?? 0,
        sub: t('review360.dashboard.statPendingSub'),
        tone: 'warn',
      },
      {
        label: t('review360.dashboard.statActive'),
        value: s?.active ?? 0,
        sub: t('review360.dashboard.statActiveSub'),
        tone: 'accent',
      },
      {
        label: t('review360.dashboard.statAboutMe'),
        value: s?.aboutMe ?? 0,
        sub: t('review360.dashboard.statAboutMeSub'),
        tone: 'neutral',
      },
      {
        label: t('review360.dashboard.statCompleted'),
        value: s?.completed ?? 0,
        sub: t('review360.dashboard.statCompletedSub'),
        tone: 'accent',
      },
    ];
  }, [dashboard, t]);

  const openAction = (a: Review360DashboardAction) => {
    if (a.kind === 'nominate') navigate(`/review-360/${a.cycleId}`);
    else if (a.responseId) navigate(`/review-360/${a.cycleId}/respond/${a.responseId}`);
    else navigate(`/review-360/${a.cycleId}`);
  };

  const openCycle = (c: ReviewCycle) => {
    if (c.status === 'completed' && meId && isSubjectIn(c))
      navigate(`/review-360/${c._id}/report/${meId}`);
    else navigate(`/review-360/${c._id}`);
  };

  if (error) {
    return (
      <div className="review360-page" style={{ padding: 32 }}>
        <Alert icon={<IconAlertCircle size={16} />} title={t('common.error')} color="red">
          {t('messages.errorLoading', { item: t('review360.title') })}
        </Alert>
      </div>
    );
  }

  const cyclesCountLabel = t('review360.dashboard.cyclesCount', { count: filtered.length });

  return (
    <div className="review360-page">
      <style>{`
        .review360-page {
          --r360-bg: var(--mantine-color-body);
          --r360-surface: var(--mantine-color-body);
          --r360-surface-2: var(--mantine-color-default-hover);
          --r360-line: var(--mantine-color-default-border);
          --r360-ink: var(--mantine-color-text);
          --r360-ink-2: var(--mantine-color-text);
          --r360-muted: var(--mantine-color-dimmed);
          --r360-soft: var(--mantine-color-placeholder);
          --r360-accent: var(--mantine-primary-color-filled);
          --r360-accent-soft: var(--mantine-primary-color-light);
          --r360-accent-deep: var(--mantine-primary-color-light-color);
          --r360-warn: #F08C00;
          --r360-warn-soft: rgba(240,140,0,0.14);
          color: var(--r360-ink);
          padding: 32px 40px 60px;
          display: flex;
          flex-direction: column;
          gap: 28px;
        }
        .review360-page .num { font-variant-numeric: tabular-nums; }
        .review360-page h1, .review360-page h2, .review360-page h3 { margin: 0; font-weight: 600; color: var(--r360-ink); }
        .review360-page p { margin: 0; }

        .r360-eyebrow { font-size: 11px; color: var(--r360-muted); text-transform: uppercase; letter-spacing: 0.14em; font-weight: 600; }
        .r360-hint { font-size: 12px; color: var(--r360-muted); }
        .r360-card { background: var(--r360-surface); border: 1px solid var(--r360-line); border-radius: 12px; }
        .r360-card-head { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid var(--r360-line); }
        .r360-card-title { font-size: 18px; font-weight: 600; }

        .r360-dot { width: 7px; height: 7px; border-radius: 999px; display: inline-block; }
        .r360-dot-accent { background: var(--r360-accent); }
        .r360-dot-warn { background: var(--r360-warn); }
        .r360-dot-neutral { background: var(--r360-muted); }
        .r360-dot-ghost { background: var(--r360-soft); }
        .r360-dot-mini { width: 3px; height: 3px; background: var(--r360-soft); border-radius: 999px; display: inline-block; }

        .r360-pill { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 999px; line-height: 1; }
        .r360-pill-accent { background: var(--r360-accent-soft); color: var(--r360-accent-deep); }
        .r360-pill-warn { background: var(--r360-warn-soft); color: var(--r360-warn); }
        .r360-pill-neutral { background: var(--r360-surface-2); color: var(--r360-ink-2); }
        .r360-pill-ghost { background: transparent; color: var(--r360-muted); border: 1px solid var(--r360-line); }

        .r360-hero { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; flex-wrap: wrap; }
        .r360-hero h1 { font-size: 30px; font-weight: 700; letter-spacing: -0.01em; line-height: 1.15; margin-bottom: 6px; }
        .r360-hero p { font-size: 14px; color: var(--r360-muted); max-width: 560px; line-height: 1.5; }
        .r360-hero-accent { color: var(--r360-accent-deep); }
        .r360-hero-actions { display: flex; gap: 8px; }


        .r360-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        @media (max-width: 900px) { .r360-stats { grid-template-columns: repeat(2, 1fr); } }
        .r360-stat { padding: 20px; }
        .r360-stat-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
        .r360-stat-value { font-size: 30px; font-weight: 700; letter-spacing: -0.02em; line-height: 1; }
        .r360-stat-sub { font-size: 12px; color: var(--r360-muted); margin-top: 6px; }

        .r360-actions { display: flex; flex-direction: column; container-type: inline-size; }
        .r360-action { display: grid; grid-template-columns: 36px 1fr 170px 130px 100px; gap: 16px; align-items: center; padding: 14px 24px; border: none; background: transparent; cursor: pointer; text-align: left; font: inherit; color: inherit; border-bottom: 1px solid var(--r360-line); }
        .r360-action-last { border-bottom: none; }
        .r360-action:hover { background: var(--r360-surface-2); }
        .r360-action-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
        .r360-action-icon-accent { background: var(--r360-accent-soft); color: var(--r360-accent-deep); }
        .r360-action-icon-warn { background: var(--r360-warn-soft); color: var(--r360-warn); }
        .r360-action-icon-neutral { background: var(--r360-surface-2); color: var(--r360-ink-2); }
        .r360-action-body { min-width: 0; }
        .r360-action-title { font-size: 14px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .r360-action-meta { font-size: 12px; color: var(--r360-muted); margin-top: 2px; display: flex; align-items: center; gap: 6px; }
        .r360-action-progress-head { display: flex; justify-content: space-between; margin-bottom: 4px; }
        .r360-action-progress-count { font-size: 11px; font-weight: 600; }
        .r360-action-progress-of { color: var(--r360-soft); font-weight: 500; }
        .r360-action-cta { display: inline-flex; align-items: center; gap: 4px; justify-self: end; color: var(--r360-accent-deep); font-size: 13px; font-weight: 500; }
        .r360-action-cta-text { white-space: nowrap; }
        /* Карточка стоит в колонке рядом с «Как это работает», поэтому узкой бывает и на широком экране. */
        @container (max-width: 640px) { .r360-action { grid-template-columns: 36px 1fr auto; row-gap: 8px; } .r360-action-progress, .r360-action-deadline { grid-column: 2 / -1; } .r360-action-cta { grid-column: 2 / -1; justify-self: start; } }

        .r360-progress { height: 4px; background: var(--r360-surface-2); border-radius: 999px; overflow: hidden; }
        .r360-progress-fill { height: 100%; background: var(--r360-accent); transition: width 200ms ease; }

        .r360-filter-bar { display: flex; justify-content: space-between; align-items: center; gap: 16px; flex-wrap: wrap; }
        .r360-tabs { display: inline-flex; gap: 4px; padding: 4px; background: var(--r360-surface); border: 1px solid var(--r360-line); border-radius: 10px; }
        .r360-tab { padding: 7px 14px; font-size: 13px; font-weight: 500; border: none; border-radius: 7px; background: transparent; color: var(--r360-muted); cursor: pointer; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; font-family: inherit; }
        .r360-tab-active { background: var(--r360-accent-soft); color: var(--r360-accent-deep); }
        .r360-tab-count { font-size: 11px; opacity: 0.7; }
        .r360-search { display: inline-flex; align-items: center; gap: 8px; height: 38px; padding: 0 12px; border-radius: 8px; border: 1px solid var(--r360-line); background: var(--r360-surface); color: var(--r360-muted); min-width: 280px; }
        .r360-search input { flex: 1; border: none; outline: none; background: transparent; font-size: 13px; color: var(--r360-ink); font-family: inherit; }

        .r360-section-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px; }

        .r360-cycle { display: block; width: 100%; background: var(--r360-surface); border: 1px solid var(--r360-line); border-radius: 12px; padding: 0; cursor: pointer; transition: border-color 160ms, box-shadow 160ms; text-align: left; font: inherit; color: inherit; }
        .r360-cycle:hover { border-color: var(--r360-accent); box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
        .r360-cycle-main { display: grid; grid-template-columns: 1.6fr 1fr 200px; gap: 24px; padding: 20px 24px; align-items: flex-start; }
        @media (max-width: 1000px) { .r360-cycle-main { grid-template-columns: 1fr; } }
        .r360-cycle-info { min-width: 0; }
        .r360-cycle-pills { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; flex-wrap: wrap; }
        .r360-cycle-title { font-size: 17px; font-weight: 600; margin-bottom: 8px; letter-spacing: -0.005em; }
        .r360-cycle-meta { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--r360-muted); flex-wrap: wrap; }
        .r360-cycle-side { display: flex; flex-direction: column; align-items: flex-end; gap: 10px; }
        @media (max-width: 1000px) { .r360-cycle-side { align-items: flex-start; } }

        .r360-cta { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 8px; font-size: 13px; font-weight: 500; }
        .r360-cta-primary { background: var(--r360-accent); color: #fff; }
        .r360-cta-secondary { background: var(--r360-surface); border: 1px solid var(--r360-line); color: var(--r360-ink); }

        .r360-cycle-subjects { min-width: 0; }
        .r360-avatars { display: flex; align-items: center; }
        .r360-avatar { width: 28px; height: 28px; border-radius: 999px; background: var(--r360-surface-2); color: var(--r360-ink-2); border: 2px solid var(--r360-surface); display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; overflow: hidden; }
        .r360-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .r360-avatar-extra { margin-left: -8px; width: 28px; height: 28px; border-radius: 999px; background: var(--r360-surface-2); color: var(--r360-ink-2); border: 2px solid var(--r360-surface); display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; }

        .r360-grid { display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 24px; align-items: flex-start; }
        @media (max-width: 1100px) { .r360-grid { grid-template-columns: 1fr; } }
        .r360-col-main { display: flex; flex-direction: column; gap: 24px; min-width: 0; }
        .r360-rail { display: flex; flex-direction: column; gap: 16px; position: sticky; top: 16px; }

        .r360-cycles { display: flex; flex-direction: column; gap: 12px; }
        .r360-empty { padding: 48px; text-align: center; color: var(--r360-muted); font-size: 14px; }

        .r360-result-title { font-size: 16px; font-weight: 600; margin-top: 4px; }
        .r360-result-footer { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; padding-top: 14px; border-top: 1px solid var(--r360-line); }
        .r360-result-score { font-size: 22px; font-weight: 700; margin-top: 2px; }
        .r360-result-score-max { font-size: 12px; color: var(--r360-muted); font-weight: 500; }
        .r360-radar-empty { padding: 32px; text-align: center; color: var(--r360-muted); font-size: 13px; background: var(--r360-surface-2); border-radius: 8px; }

        .r360-hiw { display: flex; flex-direction: column; gap: 14px; margin-top: 12px; }
        .r360-hiw-row { display: grid; grid-template-columns: 24px 1fr; gap: 12px; align-items: flex-start; }
        .r360-hiw-num { width: 22px; height: 22px; border-radius: 999px; background: var(--r360-accent-soft); color: var(--r360-accent-deep); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; }
        .r360-hiw-title { font-size: 13px; font-weight: 500; }
        .r360-hiw-desc { font-size: 12px; color: var(--r360-muted); margin-top: 2px; line-height: 1.5; }
      `}</style>

      <Hero
        pendingCount={dashboard?.stats.pending ?? 0}
        canCreateCycle={canCreateCycle}
        canManageTemplates={canManageTemplates}
        onCreateCycle={() => setIsCreateOpen(true)}
        onOpenTemplates={() => navigate('/review-360/templates')}
      />

      <StatStrip stats={stats} />

      <div className="r360-grid">
        <div className="r360-col-main">
          <ActionsBlock actions={dashboard?.actions ?? []} onOpenAction={openAction} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="r360-section-head">
              <div>
                <div className="r360-eyebrow">{t('review360.dashboard.cyclesEyebrow')}</div>
                <h2 className="r360-card-title">{cyclesCountLabel}</h2>
              </div>
            </div>
            <FilterBar
              filter={filter}
              onChange={setFilter}
              search={search}
              onSearch={setSearch}
              counts={counts}
            />
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <Loader size="sm" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="r360-card r360-empty">{t('review360.dashboard.cycleEmpty')}</div>
            ) : (
              <div className="r360-cycles">
                {filtered.map((c) => (
                  <CycleCard
                    key={c._id}
                    cycle={c}
                    iAmSubject={isSubjectIn(c)}
                    onOpen={() => openCycle(c)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="r360-rail">
          {dashboard?.latestResult && (
            <LatestResultCard
              result={dashboard.latestResult}
              onOpen={() =>
                navigate(`/review-360/${dashboard.latestResult!.cycleId}/report/${meId || ''}`)
              }
            />
          )}
          <HowItWorksCard />
        </div>
      </div>

      {/* Окно тянет шаблоны и людей — только тем, кто может запустить цикл. */}
      {canCreateCycle && (
        <CycleCreateModal opened={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
      )}
    </div>
  );
};
